// 📁 components/categoria/CategoriaContainer.js - IMPORT CORREGIDO
import React, { useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Fab
} from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { ThemeProvider } from "@mui/material/styles";

// Imports de componentes
import AsociacionNavigation from "./AsociacionNavigation";
import CategoriaList from "./CategoriaList";
import SearchDrawer from "./SearchDrawer";
import DetailsModal from "./DetailsModal";
import ExportButtons from "./ExportButtons"; // ← VERIFICAR QUE EXISTE EL ARCHIVO

// Imports de hooks y utils
import { useCategoriaData } from "../../hooks/useCategoriaData";
import { useCategoriaState } from "../../hooks/useCategoriaState";

// Imports de estilos
import {
  theme,
  ResponsiveMainLayout,
  MainContentResponsive,
  ContentAreaResponsive,
  SearchFab
} from "../../styles/CategoriaStyles";

const CategoriaContainer = () => {
  // Custom hooks para manejo de datos y estado
  const {
    categorias,
    representantes,
    asociaciones,
    departamentos,
    municipios,
    isLoading,
    error,
    refetch
  } = useCategoriaData();

  const {
    selectedAsociacion,
    selectedCategoria,
    expandedCategorias,
    expandedGubernamental,
    vistaJuntaDirectiva,
    openModal,
    drawerOpen,
    snackbar,
    setExpandedGubernamental,
    setDrawerOpen,
    handleSelectAsociacion,
    handleToggleCategoria,
    handleOpenModal,
    handleCloseModal,
    handleVistaJuntaChange,
    showSnackbar,
    hideSnackbar
  } = useCategoriaState();

  // Effect para mostrar mensaje de éxito cuando los datos se cargan
  useEffect(() => {
    if (!isLoading && !error && categorias.length > 0) {
      showSnackbar(
        `Datos cargados exitosamente: ${categorias.length} categorías, ${representantes.length} representantes`,
        "success"
      );
    }
  }, [isLoading, error, categorias.length, representantes.length]);

  // Effect para mostrar errores
  useEffect(() => {
    if (error) {
      showSnackbar(`Error: ${error}`, "error");
    }
  }, [error]);

  // Loading state
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          justifyContent: "center", 
          alignItems: "center", 
          height: "100vh", 
          backgroundColor: theme.palette.background.section 
        }}>
          <CircularProgress 
            sx={{ 
              color: theme.palette.primary.main, 
              mb: 2 
            }} 
            size={60} 
          />
          <Typography sx={{ 
            fontWeight: 600, 
            color: theme.palette.text.primary 
          }}>
            Cargando Datos...
          </Typography>
          <Typography variant="body2" sx={{ 
            color: theme.palette.text.secondary,
            mt: 1
          }}>
            Obteniendo información de representantes y asociaciones
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Error state
  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ 
          p: 4, 
          textAlign: "center",
          backgroundColor: theme.palette.background.section,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <Typography variant="h5" sx={{ 
            color: theme.palette.accent.main, 
            fontWeight: 700, 
            mb: 2 
          }}>
            Error al cargar los datos
          </Typography>
          <Typography sx={{ 
            color: theme.palette.text.secondary, 
            mb: 3,
            maxWidth: 400
          }}>
            {error}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <button 
              onClick={refetch}
              style={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600
              }}
            >
              Reintentar
            </button>
            <button 
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: "transparent",
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600
              }}
            >
              Recargar Página
            </button>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ResponsiveMainLayout>
        {/* Navegación de Asociaciones */}
        <AsociacionNavigation
          asociaciones={asociaciones}
          representantes={representantes}
          selectedAsociacion={selectedAsociacion}
          onSelectAsociacion={handleSelectAsociacion}
          expandedGubernamental={expandedGubernamental}
          setExpandedGubernamental={setExpandedGubernamental}
        />

        {/* Contenido Principal */}
        <MainContentResponsive>
          {/* Header del contenido */}
          <Box sx={{ 
            mb: 3, 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "flex-start",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 }
          }}>
            <Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: theme.palette.primary.main, 
                mb: 1,
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}>
                {selectedAsociacion 
                  ? selectedAsociacion.nombre_abreviado 
                  : "ORGANIZACIÓN DE REPRESENTANTES"
                }
              </Typography>
              <Typography variant="subtitle1" sx={{ 
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.9rem", md: "1rem" }
              }}>
                {selectedAsociacion 
                  ? `Representantes de ${selectedAsociacion.nombre_abreviado}` 
                  : "Selecciona una asociación para ver sus representantes"
                }
              </Typography>
            </Box>

            {/* BOTÓN DE EXPORTACIÓN PDF - AHORA DESCOMENTADO */}
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-end", md: "flex-start" }
            }}>
             <ExportButtons
  selectedAsociacion={selectedAsociacion}
  selectedCategoria={selectedCategoria}
  representantes={representantes}
  categorias={categorias}
  asociaciones={asociaciones}
  departamentos={departamentos}
  municipios={municipios} // ⭐ AGREGAR ESTA LÍNEA
  context="general"
/>
            </Box>
          </Box>

          {/* Lista de Categorías */}
          <ContentAreaResponsive>
            <CategoriaList
              selectedAsociacion={selectedAsociacion}
              representantes={representantes}
              categorias={categorias}
              departamentos={departamentos}
              municipios={municipios}
              expandedCategorias={expandedCategorias}
              onToggleCategoria={handleToggleCategoria}
              vistaJuntaDirectiva={vistaJuntaDirectiva}
              onVistaJuntaChange={handleVistaJuntaChange}
            />
          </ContentAreaResponsive>
        </MainContentResponsive>

        {/* FAB de Búsqueda */}
        <SearchFab 
          onClick={() => setDrawerOpen(true)} 
          aria-label="buscar representante"
        >
          <PersonSearchIcon />
        </SearchFab>

        {/* Drawer de Búsqueda */}
        <SearchDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          representantes={representantes}
          categorias={categorias}
          asociaciones={asociaciones} 
        />

        {/* Modal de Detalles - CON EXPORTACIÓN HABILITADA */}
        <DetailsModal
          open={openModal}
          onClose={handleCloseModal}
          categoria={selectedCategoria}
          representantes={representantes}
          asociaciones={asociaciones}
          departamentos={departamentos}
          extraActions={selectedCategoria && (
            <ExportButtons
  selectedCategoria={selectedCategoria}
  representantes={representantes.filter(rep =>
    rep.categorias?.some(cat => cat.Id === selectedCategoria.Id && cat.Estado !== 'Inactivo')
  )}
  categorias={categorias}
  asociaciones={asociaciones}
  departamentos={departamentos}
  municipios={municipios} // ⭐ AGREGAR ESTA LÍNEA
  context="categoria"
/>
          )}
        />

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={hideSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={hideSnackbar}
            severity={snackbar.severity}
            sx={{ 
              width: "100%", 
              borderRadius: "12px" 
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ResponsiveMainLayout>
    </ThemeProvider>
  );
};

export default CategoriaContainer;